import { Col, Drawer, Grid, Layout, Row, Skeleton } from "antd";
import { useAuth } from "global/context/AuthenticationContext";
import { Route } from "navigation/AppMenu";
import React, {
  Suspense,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import HeaderAppLayout from "./HeaderAppLayout";
import SideNavigation from "./SideNavigation";

const { useBreakpoint } = Grid;

const { Header, Content, Sider } = Layout;

const getMenuItem = (
  label: any,
  key: any,
  code: any,
  icon?: React.ReactNode,
  children: any = null,
  type: any = null,
) => {
  return {
    key,
    code,
    icon,
    children,
    label,
    type,
  };
};

function hasItem(nav: any): nav is { item: Route; children?: Route[] } {
  return nav && typeof nav.item !== "undefined";
}

function Main() {
  const navigate = useNavigate();

  const { config } = useAuth();
  const { pathname } = useLocation();
  const screens = useBreakpoint();

  let path = pathname.replace?.("/", "");

  const [isShowDrawerMenu, setDrawerVisible] = useState(false);
  const isDesktop = screens.xl;

  const sideNav = useMemo(() => {
    const createChildren = (subMenu: any[]): Route[] | undefined => {
      if (!subMenu || subMenu.length === 0) return undefined;

      return subMenu.map((subMenuItem) => {
        return new Route(
          subMenuItem.path,
          subMenuItem.name,
          subMenuItem.path,
          subMenuItem.icon,
          subMenuItem.componentName,
          subMenuItem.code,
          createChildren(subMenuItem?.subMenu),
        );
      });
    };

    const sideNav = config.menu.map((menu) => {
      const children = createChildren(menu.subMenu);

      if (children && children.length > 0) {
        return {
          item: new Route(
            menu.path,
            menu.name,
            menu.path,
            menu.icon,
            menu.componentName,
            menu.code,
            children,
          ),
          children: children,
        };
      } else {
        return new Route(
          menu.path,
          menu.name,
          menu.path,
          menu.icon,
          menu.componentName,
          menu.code,
        );
      }
    });

    return sideNav;
  }, [config]);

  const itemsNav = useMemo(() => {
    return sideNav.map((nav) => {
      if (hasItem(nav)) {
        const iconElement = nav.item.icon
          ? React.createElement(nav.item.icon)
          : null;

        const hasChildren = nav.children && nav.children.length > 0;
        if (!hasChildren && nav.item.componentName) {
          return getMenuItem(
            nav.item.name,
            nav.item.path,
            nav.item.code,
            iconElement,
          );
        }

        return getMenuItem(
          nav.item.name,
          nav.item.path,
          nav.item.code,
          iconElement,
          nav.children?.map((subNav) =>
            getMenuItem(subNav.name, subNav.path, subNav.code),
          ),
        );
      } else {
        const iconElement = nav.icon ? React.createElement(nav.icon) : null;

        return getMenuItem(nav.name, nav.path, nav.code, iconElement);
      }
    });
  }, [sideNav]);

  const toggleMenu = () => {
    setDrawerVisible(!isShowDrawerMenu);
  };

  const add = (route: Route) => {
    if (!route?.componentName) {
      return;
    }

    navigate(route.path.startsWith("/") ? route.path : `/${route.path}`);
  };

  const getItem = useCallback(
    (path: string): Route | undefined => {
      let foundedItem: Route | undefined;

      sideNav.forEach((e: any) => {
        if (hasItem(e)) {
          if (e.item.path.includes(path)) {
            foundedItem = e.item;
          }

          e.children?.forEach((child: any) => {
            if (child.path.includes(path)) {
              foundedItem = child;
            }
          });
        } else {
          if (e.path.includes(path)) {
            foundedItem = e;
          }
        }
      });

      return foundedItem;
    },
    [sideNav],
  );

  const route = getItem(path);

  const renderDrawerMode = () => {
    return (
      <Drawer
        title={false}
        placement="left"
        closable={false}
        onClose={() => setDrawerVisible(false)}
        open={isShowDrawerMenu}
        key="left"
        width={280}
        className="drawer-sidebar"
        style={{ background: "#FFC427" }}
      >
        <Layout>
          <Sider
            collapsedWidth="0"
            trigger={null}
            style={{ background: "#FFC427" }}
            width={250}
            theme="light"
          >
            <SideNavigation
              sideNav={sideNav}
              itemsNav={itemsNav}
              onClick={add}
            />
          </Sider>
        </Layout>
      </Drawer>
    );
  };

  const renderNormalMode = () => {
    return (
      <Layout
        style={{
          position: "relative",
        }}
        className="full-height"
      >
        {isDesktop && (
          <Sider
            collapsedWidth="0"
            trigger={null}
            width={250}
            theme="light"
            className="sidebar"
          >
            <SideNavigation
              sideNav={sideNav}
              itemsNav={itemsNav}
              onClick={add}
            />
          </Sider>
        )}

        <Layout
          className={`custom-override transition-all duration-300 ${
            isDesktop ? "ml-[250px]" : "ml-0"
          }`}
          style={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Header
            style={{
              backgroundColor: "white",
              padding: 0,
              lineHeight: "normal",
              flexShrink: 0,
            }}
            className="safe-top !shadow-[0_4px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05)] border-b border-gray-100 z-50 sticky top-0"
          >
            <HeaderAppLayout onPress={toggleMenu} />
          </Header>
          <Content className="content-safe scroll-content">
            <div style={{ flex: 1 }}>
              <Row gutter={[0, 0]}>
                <Col span={24} className="custom-override">
                  <Suspense fallback={<Skeleton active />}>
                    {/* {React.createElement(
                    route?.componentName
                      ? AppPages.getPage(route.componentName)
                      : UnknownPage
                  )} */}
                    <Outlet />
                  </Suspense>
                </Col>
              </Row>
            </div>
          </Content>
        </Layout>
      </Layout>
    );
  };

  return (
    <Layout style={{ height: "100%", position: "relative" }}>
      {renderDrawerMode()}
      {renderNormalMode()}
    </Layout>
  );
}

export default Main;
